import { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    Button,
    Typography,
    Paper,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    Box,
    Stack,
    IconButton,
    Avatar,
    Switch,
    Divider,
    Container
} from '@mui/material';
import { CloudUpload, ArrowBack, Save, Close, Add } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Hooks and Services
import { useProductDetails, useUpdateProduct } from 'hooks/useProduct';
import { useProductCategoriesForSelection } from 'hooks/useProductCategories';
import productService from 'api/productService';
import { useCurrentVendor } from 'hooks/useUser';
import LocationWarningDialog from '../settings/LocationWarningDialog';

const standardUnits = ['pieces', 'kg', 'plates', 'liters', 'packs'];

const validationSchema = Yup.object({
    name: Yup.string().required('Product name is required'),
    category: Yup.string().required('Category is required'),
    price: Yup.number().typeError('Must be a number').positive('Must be positive').required('Required'),
    description: Yup.string().required('Description is required'),
    thumbnail: Yup.string().required('Main image is required'),
    unitOfMeasure: Yup.string().max(15, 'Too long (max 15 chars)').required('Required'),
    stock: Yup.number().min(0, 'Cannot be negative').required('Required'),
    available: Yup.boolean(),
    prepTime: Yup.number().typeError('Must be a number').min(0, 'Cannot be negative').required('Preparation time is required')
});

const VendorEditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // UI States
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const [isCustomUnit, setIsCustomUnit] = useState(false);
    const [uploadingThumb, setUploadingThumb] = useState(false);

    // Data Fetching
    const { data: productData, isLoading: loadingProduct } = useProductDetails(id);
    const { data: categoryResponse } = useProductCategoriesForSelection({ limit: 1000 });
    const { mutate: updateProduct, isPending: isUpdating } = useUpdateProduct();
    const { data: vendorData, isLoading: loadingVendor } = useCurrentVendor();

    const categories = categoryResponse?.data || [];

    const formik = useFormik({
        initialValues: {
            name: '',
            category: '',
            price: '',
            description: '',
            thumbnail: '',
            images: [],
            stock: 0,
            unitOfMeasure: 'pieces',
            available: false,
            prepTime: 15
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const payload = {
                ...values,
                price: Number(values.price),
                stock: Number(values.stock)
            };
            updateProduct(
                { id, ...payload },
                {
                    onSuccess: () => {
                        setToast({ open: true, message: 'Product updated successfully!', severity: 'success' });
                        setTimeout(() => navigate('/vendor/products'), 1500);
                    },
                    onError: (err) => {
                        setToast({ open: true, message: err?.response?.data?.message || 'Update failed', severity: 'error' });
                    }
                }
            );
        }
    });

    // Handle Data Hydration and Custom Unit Detection
    useEffect(() => {
        if (productData) {
            const unit = productData.unitOfMeasure || 'pieces';
            if (!standardUnits.includes(unit)) {
                setIsCustomUnit(true);
            }
            formik.setValues({
                name: productData.name || '',
                category: productData.category?._id || productData.category || '',
                price: productData.price || '',
                description: productData.description || '',
                thumbnail: productData.thumbnail || '',
                images: productData.images || [],
                stock: productData.stock || 0,
                unitOfMeasure: unit,
                available: productData.available ?? productData.isAvailable ?? false,
                prepTime: productData.prepTime || 15
            });
        }
    }, [productData, formik]);

    const handleThumbnailUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploadingThumb(true);
            const res = await productService.uploadFile(file);
            formik.setFieldValue('thumbnail', res.url);
        } catch (err) {
            setToast({ open: true, message: 'Upload failed', severity: 'error' });
        } finally {
            setUploadingThumb(false);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(formik.values.images);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        formik.setFieldValue('images', items);
    };

    if (loadingProduct || loadingVendor)
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );

    const vendor = vendorData?.vendor;
    if (!vendor?.location || !vendor?.location?.coordinates || vendor?.location?.coordinates?.length !== 2) {
        return <LocationWarningDialog open={true} onClose={() => navigate('/vendor/products')} />;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={toast.severity} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Header */}
            <Stack direction="row" spacing={2} mb={4} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" fontWeight={800} letterSpacing={-1}>
                        Edit Product
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={formik.handleSubmit}
                    disabled={isUpdating || uploadingThumb}
                    sx={{ borderRadius: 2, px: 4 }}
                >
                    Save Changes
                </Button>
            </Stack>

            <Grid container spacing={4}>
                {/* Left Side: Form Details */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 4, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label="Product Name"
                                {...formik.getFieldProps('name')}
                                error={formik.touched.name && !!formik.errors.name}
                                helperText={formik.touched.name && formik.errors.name}
                            />

                            <TextField
                                select
                                fullWidth
                                label="Category"
                                {...formik.getFieldProps('category')}
                                error={formik.touched.category && !!formik.errors.category}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Grid container spacing={0.5}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Price (₦)"
                                        {...formik.getFieldProps('price')}
                                        error={formik.touched.price && !!formik.errors.price}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Stock Quantity"
                                        {...formik.getFieldProps('stock')}
                                        error={formik.touched.stock && !!formik.errors.stock}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {!isCustomUnit ? (
                                        <TextField
                                            select
                                            fullWidth
                                            label="Unit"
                                            {...formik.getFieldProps('unitOfMeasure')}
                                            onChange={(e) => {
                                                if (e.target.value === 'custom_trigger') {
                                                    setIsCustomUnit(true);
                                                    formik.setFieldValue('unitOfMeasure', '');
                                                } else {
                                                    formik.handleChange(e);
                                                }
                                            }}
                                        >
                                            {standardUnits.map((u) => (
                                                <MenuItem key={u} value={u}>
                                                    {u.charAt(0).toUpperCase() + u.slice(1)}
                                                </MenuItem>
                                            ))}
                                            <Divider />
                                            <MenuItem value="custom_trigger" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                                                + Custom Unit
                                            </MenuItem>
                                        </TextField>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Custom Unit"
                                            {...formik.getFieldProps('unitOfMeasure')}
                                            error={formik.touched.unitOfMeasure && !!formik.errors.unitOfMeasure}
                                            helperText={formik.errors.unitOfMeasure || 'e.g. Bunch, Box'}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setIsCustomUnit(false);
                                                            formik.setFieldValue('unitOfMeasure', 'pieces');
                                                        }}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Prep Time (mins)"
                                        {...formik.getFieldProps('prepTime')}
                                        error={formik.touched.prepTime && !!formik.errors.prepTime}
                                        helperText={formik.touched.prepTime && formik.errors.prepTime}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={4}
                                {...formik.getFieldProps('description')}
                                error={formik.touched.description && !!formik.errors.description}
                            />
                        </Stack>
                    </Paper>

                    {/* Gallery Section */}
                    <Typography variant="h6" fontWeight={700} mt={4} mb={2}>
                        Product Gallery
                    </Typography>
                    <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="gallery" direction="horizontal">
                                {(provided) => (
                                    <Grid container spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
                                        {formik.values.images.map((url, i) => (
                                            <Draggable key={url} draggableId={url} index={i}>
                                                {(provided) => (
                                                    <Grid
                                                        item
                                                        xs={4}
                                                        sm={3}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <Box sx={{ position: 'relative' }}>
                                                            <Avatar
                                                                src={url}
                                                                variant="rounded"
                                                                sx={{ width: '100%', height: 80, border: '2px solid #eee' }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const newImgs = formik.values.images.filter((_, idx) => idx !== i);
                                                                    formik.setFieldValue('images', newImgs);
                                                                }}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: -10,
                                                                    right: -10,
                                                                    bgcolor: 'error.main',
                                                                    color: 'white',
                                                                    '&:hover': { bgcolor: 'error.dark' }
                                                                }}
                                                            >
                                                                <Close sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        <Grid item xs={4} sm={3}>
                                            <Button
                                                component="label"
                                                sx={{
                                                    width: '100%',
                                                    height: 80,
                                                    border: '2px dashed #ccc',
                                                    borderRadius: 2,
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <Add />
                                                <Typography variant="caption">Add</Typography>
                                                <input
                                                    type="file"
                                                    hidden
                                                    multiple
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        const files = Array.from(e.target.files);
                                                        const urls = await Promise.all(files.map((f) => productService.uploadFile(f)));
                                                        formik.setFieldValue('images', [
                                                            ...formik.values.images,
                                                            ...urls.map((r) => r.url)
                                                        ]);
                                                    }}
                                                />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Paper>
                </Grid>

                {/* Right Side: Media & Status */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3, borderRadius: 4, textAlign: 'center', border: '1px solid #eee' }} elevation={0}>
                            <Typography variant="subtitle2" textAlign="left" mb={2}>
                                Thumbnail (Cover)
                            </Typography>
                            <Avatar
                                src={formik.values.thumbnail}
                                variant="rounded"
                                sx={{ width: '100%', height: 240, mb: 2, bgcolor: '#fafafa' }}
                            />
                            <Button component="label" variant="outlined" fullWidth startIcon={<CloudUpload />}>
                                {uploadingThumb ? 'Uploading...' : 'Replace Cover'}
                                <input type="file" hidden accept="image/*" onChange={handleThumbnailUpload} />
                            </Button>
                        </Paper>

                        <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        Availability
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Visible to customers
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={formik.values.available}
                                    onChange={(e) => formik.setFieldValue('available', e.target.checked)}
                                    color="success"
                                />
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
};

export default VendorEditProduct;
